import * as React from 'react'
import useMeasure from 'react-use-measure'
import { useDomEvent, useWindowDomEvent } from 'hooks/use-dom-event'
import { clamp } from 'utils/math'

export type ResizableColumnBound = number | `${number}%`

interface UseResizableColumnOptions {
  currentWidth: number
  minWidth: ResizableColumnBound
  maxWidth: ResizableColumnBound
  onChange: (width: number) => void
  /** When false, the persisted width isn't re-clamped — e.g. while the column is collapsed. */
  enabled?: boolean
  /** Dragging left grows the column. Use for a handle on the left edge of a right-docked panel. */
  invert?: boolean
  cancelOnWindowBlur?: boolean
  onDragStart?: () => void
  onDragEnd?: () => void
  onDragCancel?: () => void
}

interface ResizableColumnHandleProps {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void
}

interface UseResizableColumnResult {
  isDragging: boolean
  /** Attach to the element that percentage bounds are measured against. */
  containerRef: ReturnType<typeof useMeasure>[0]
  /** Bounds resolved to pixels — pass to the handle so its ARIA match the drag. */
  resolvedMinWidth: number
  resolvedMaxWidth: number
  handleProps: ResizableColumnHandleProps
}

interface DragStart {
  pointerX: number
  startWidth: number
  pointerId: number
  target: HTMLElement
}

function resolveBoundToPixels(
  bound: ResizableColumnBound,
  containerWidth: number,
  fallbackWidth: number,
): number {
  if (typeof bound === 'number') {
    return bound
  }
  if (containerWidth <= 0) {
    return fallbackWidth
  }
  return Math.round((Number.parseFloat(bound) / 100) * containerWidth)
}

const PREVIOUS_BODY_STYLE: { cursor: string; userSelect: string } = {
  cursor: '',
  userSelect: '',
}

function lockBodyCursor() {
  PREVIOUS_BODY_STYLE.cursor = document.body.style.cursor
  PREVIOUS_BODY_STYLE.userSelect = document.body.style.userSelect
  document.body.style.cursor = 'col-resize'
  document.body.style.userSelect = 'none'
}

function unlockBodyCursor() {
  document.body.style.cursor = PREVIOUS_BODY_STYLE.cursor
  document.body.style.userSelect = PREVIOUS_BODY_STYLE.userSelect
}

function releasePointerCapture(start: DragStart | null) {
  if (start?.target.hasPointerCapture(start.pointerId)) {
    start.target.releasePointerCapture(start.pointerId)
  }
}

export function useResizableColumn({
  currentWidth,
  minWidth,
  maxWidth,
  onChange,
  enabled = true,
  invert = false,
  cancelOnWindowBlur = false,
  onDragStart,
  onDragEnd,
  onDragCancel,
}: UseResizableColumnOptions): UseResizableColumnResult {
  const [containerRef, containerBounds] = useMeasure()
  const resolvedMinWidth = resolveBoundToPixels(minWidth, containerBounds.width, currentWidth)
  const resolvedMaxWidth = resolveBoundToPixels(maxWidth, containerBounds.width, currentWidth)

  const [isDragging, setIsDragging] = React.useState(false)
  const dragStart = React.useRef<DragStart | null>(null)

  const latestOnChange = React.useRef(onChange)
  latestOnChange.current = onChange
  const latestBounds = React.useRef({ resolvedMinWidth, resolvedMaxWidth, invert })
  latestBounds.current = { resolvedMinWidth, resolvedMaxWidth, invert }
  const latestDragLife = React.useRef({ onDragStart, onDragEnd, onDragCancel })
  latestDragLife.current = { onDragStart, onDragEnd, onDragCancel }

  const onPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (event.button !== 0 || !enabled) {
        return
      }
      event.preventDefault()
      event.currentTarget.setPointerCapture(event.pointerId)

      dragStart.current = {
        pointerX: event.clientX,
        startWidth: currentWidth,
        pointerId: event.pointerId,
        target: event.currentTarget,
      }
      setIsDragging(true)
      lockBodyCursor()
      latestDragLife.current.onDragStart?.()
    },
    [currentWidth, enabled],
  )

  const onPointerMove = React.useCallback((event: Event) => {
    const start = dragStart.current
    if (start === null) {
      return
    }
    const pointerX = (event as PointerEvent).clientX
    const deltaX = latestBounds.current.invert
      ? start.pointerX - pointerX
      : pointerX - start.pointerX
    const { resolvedMinWidth: min, resolvedMaxWidth: max } = latestBounds.current
    latestOnChange.current(clamp(start.startWidth + deltaX, min, max))
  }, [])

  const finishDrag = React.useCallback((commit: boolean) => {
    const start = dragStart.current
    if (start === null) {
      return
    }
    dragStart.current = null
    setIsDragging(false)
    unlockBodyCursor()
    releasePointerCapture(start)
    if (commit) {
      latestDragLife.current.onDragEnd?.()
      return
    }
    latestDragLife.current.onDragCancel?.()
  }, [])

  const onPointerUp = React.useCallback(() => {
    finishDrag(true)
  }, [finishDrag])

  const onPointerCancel = React.useCallback(() => {
    finishDrag(false)
  }, [finishDrag])

  React.useEffect(() => {
    if (!enabled) {
      return
    }
    const clampedWidth = clamp(currentWidth, resolvedMinWidth, resolvedMaxWidth)
    if (clampedWidth !== currentWidth) {
      onChange(clampedWidth)
    }
  }, [enabled, currentWidth, resolvedMinWidth, resolvedMaxWidth, onChange])

  React.useEffect(() => {
    if (!enabled && isDragging) {
      finishDrag(false)
    }
  }, [enabled, isDragging, finishDrag])

  useDomEvent(useDomEvent.document, 'pointermove', isDragging ? onPointerMove : false)
  useDomEvent(useDomEvent.document, 'pointerup', isDragging ? onPointerUp : false)
  useDomEvent(useDomEvent.document, 'pointercancel', isDragging ? onPointerCancel : false)
  useWindowDomEvent('blur', isDragging && cancelOnWindowBlur ? onPointerCancel : false)

  return {
    isDragging,
    containerRef,
    resolvedMinWidth,
    resolvedMaxWidth,
    handleProps: { onPointerDown },
  }
}
